import { IPet } from '@/shared/interfaces';
import { Button, Input, Modal, Select } from '@/components';
import { R } from '@/features/profile/ProfileResources.resx';
import { R as Resources } from '@/Resources.resx';
import { PetSexEnum, PetTypeEnum } from '@/shared/enums';
import { Utils } from '@/shared/Utils';
import { useForm } from 'react-hook-form';
import { usePetModalMutation } from './hooks/usePetModalMutation';

export type PetModalProps = {
    pet: IPet;
    onClose: (reload: boolean) => void;
};

type IFormPet = IPet & {
    BirthMonth: string;
    BirthYear: string;
};

const petTypeOptions = [
    { value: PetTypeEnum.Dog, label: R.PetTypeDog },
    { value: PetTypeEnum.Cat, label: R.PetTypeCat },
    { value: PetTypeEnum.Rabbit, label: R.PetTypeRabbit },
    { value: PetTypeEnum.Rodent, label: R.PetTypeRodent },
];

const petSexOptions = [
    { value: PetSexEnum.Female, label: R.PetSexFemale },
    { value: PetSexEnum.FemaleSterilized, label: R.PetSexFemaleSterilized },
    { value: PetSexEnum.Male, label: R.PetSexMale },
    { value: PetSexEnum.MaleNeutered, label: R.PetSexMaleNeutered },
    { value: PetSexEnum.Unknown, label: R.PetSexUnknown },
];

const months = [
    { value: 0, label: R.MonthJanuary },
    { value: 1, label: R.MonthFebruary },
    { value: 2, label: R.MonthMarch },
    { value: 3, label: R.MonthApril },
    { value: 4, label: R.MonthMay },
    { value: 5, label: R.MonthJune },
    { value: 6, label: R.MonthJuly },
    { value: 7, label: R.MonthAugust },
    { value: 8, label: R.MonthSeptember },
    { value: 9, label: R.MonthOctober },
    { value: 10, label: R.MonthNovember },
    { value: 11, label: R.MonthDecember },
];

const currentYear = new Date().getFullYear();
const maxYears = currentYear - 30;

const years = [
    ...Array.from(
        { length: currentYear - maxYears + 1 },
        (_, i) => maxYears + i,
    ).map((year) => ({ value: year, label: year.toString() })),
];

export const PetModal = ({ onClose, pet }: PetModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        trigger,
    } = useForm<IFormPet>({
        mode: 'onBlur',
        defaultValues: {
            ...(pet ?? {}),
            BirthMonth: pet?.Birthdate ? Utils.formatMonth(pet.Birthdate) : '',
            BirthYear: pet?.Birthdate ? Utils.formatYear(pet.Birthdate) : '',
        },
    });

    const isEditing = !!pet;

    const updateBirthdate = () => {
        const month = getValues('BirthMonth');
        const year = getValues('BirthYear');

        if (month && year) {
            setValue('Birthdate', new Date(Number(year), Number(month)));
        }
    };

    const { onSubmit, isPending } = usePetModalMutation({
        pet,
        onSuccess: () => onClose(true),
    });

    const onCancel = () => onClose(false);

    return (
        <Modal onClose={onCancel} title={isEditing ? R.EditPet : R.AddPet}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex h-full flex-col justify-between">
                    <main>
                        <div className="flex flex-col justify-between gap-1">
                            <Input
                                {...register('Name', {
                                    required: R.PleaseEnterPetName,
                                })}
                                label={R.PetName}
                                error={errors.Name?.message}
                            ></Input>

                            <div className="flex justify-between gap-2 py-2">
                                <div className="flex flex-col">
                                    <p
                                        id="petTypeLabel"
                                        className="ds-text-highlighted my-0 py-1 text-lg font-bold text-grey-900"
                                    >
                                        {R.PetType}
                                    </p>
                                    <Select
                                        prompt={R.SelectPetType}
                                        options={petTypeOptions}
                                        defaultValue={pet?.Type ?? ''}
                                        {...register('Type', {
                                            required: R.PleaseSelectPetType,
                                        })}
                                        onChange={(e) =>
                                            setValue(
                                                'Type',
                                                Number(e.target.value),
                                                { shouldValidate: true },
                                            )
                                        }
                                        error={errors.Type?.message}
                                    ></Select>
                                </div>

                                <div className="flex w-full flex-col">
                                    <p
                                        id="petSexLabel"
                                        className="ds-text-highlighted my-0 py-1 text-lg font-bold text-grey-900"
                                    >
                                        {R.PetSex}
                                    </p>
                                    <Select
                                        className="w-full"
                                        prompt={R.SelectPetSex}
                                        options={petSexOptions}
                                        defaultValue={pet?.Sex ?? ''}
                                        {...register('Sex', {
                                            required: R.PleaseSelectPetSex,
                                        })}
                                        onChange={(e) =>
                                            setValue(
                                                'Sex',
                                                Number(e.target.value),
                                                { shouldValidate: true },
                                            )
                                        }
                                        error={errors.Sex?.message}
                                    ></Select>
                                </div>
                            </div>

                            <div className="py-2">
                                <p className="ds-text-highlighted my-0 py-1 text-lg font-bold text-grey-900">
                                    {R.PetBirthdate}
                                </p>
                                <div className="flex justify-between gap-2">
                                    <div className="w-1/2">
                                        <Select
                                            className="w-full"
                                            prompt={R.SelectMonth}
                                            options={months}
                                            defaultValue={
                                                pet
                                                    ? Utils.formatMonth(
                                                          pet.Birthdate,
                                                      )
                                                    : ''
                                            }
                                            {...register('BirthMonth', {
                                                validate: (value) => {
                                                    if (!value)
                                                        return R.PleaseSelectMonth;

                                                    const date = new Date(
                                                        Number(
                                                            getValues(
                                                                'BirthYear',
                                                            ),
                                                        ),
                                                        Number(value),
                                                    );
                                                    return (
                                                        date <= new Date() ||
                                                        Resources.ErrorDateInTheFuture
                                                    );
                                                },
                                            })}
                                            onChange={(e) => {
                                                setValue(
                                                    'BirthMonth',
                                                    e.target.value,
                                                    { shouldValidate: true },
                                                );
                                                updateBirthdate();
                                            }}
                                            error={errors.BirthMonth?.message}
                                        ></Select>
                                    </div>

                                    <div className="w-1/2">
                                        <Select
                                            className="w-full"
                                            prompt={R.SelectYear}
                                            options={years}
                                            defaultValue={
                                                pet
                                                    ? Utils.formatYear(
                                                          pet.Birthdate,
                                                      )
                                                    : ''
                                            }
                                            {...register('BirthYear', {
                                                validate: (value) => {
                                                    if (!value)
                                                        return R.PleaseSelectYear;

                                                    const date = new Date(
                                                        Number(value),
                                                        Number(
                                                            getValues(
                                                                'BirthMonth',
                                                            ),
                                                        ),
                                                    );
                                                    return (
                                                        date <= new Date() || ''
                                                    );
                                                },
                                            })}
                                            onChange={(e) => {
                                                setValue(
                                                    'BirthYear',
                                                    e.target.value,
                                                );
                                                trigger([
                                                    'BirthYear',
                                                    'BirthMonth',
                                                ]);
                                                updateBirthdate();
                                            }}
                                            error={errors.BirthYear?.message}
                                        ></Select>
                                    </div>
                                </div>
                                <div className="text-left text-sm text-grey-600">
                                    {R.PetBirthdateHint}
                                </div>
                            </div>

                            <Input
                                label={R.PetWeight}
                                placeholder={R.PetWeightPlaceholder}
                                {...register('WeightKg', {
                                    valueAsNumber: false,
                                    setValueAs: Utils.normalizeNumber,
                                    validate: (value) =>
                                        Utils.isNumberOrEmpty(
                                            value?.toString(),
                                        ) || R.PleaseEnterANumber,
                                })}
                                error={errors.WeightKg?.message}
                            ></Input>

                            <Input
                                label={R.PetRace}
                                placeholder={R.PetRacePlaceholder}
                                {...register('Race')}
                            ></Input>
                        </div>
                    </main>

                    <footer className="text-right">
                        <Button
                            variant="primary"
                            type="submit"
                            className={isPending ? 'button--loading' : ''}
                        >
                            {isEditing ? R.ConfirmEditPet : R.ConfirmAddPet}
                        </Button>
                    </footer>
                </div>
            </form>
        </Modal>
    );
};
